import {
  createContext,
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  on,
  useContext,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import {
  getUserRankHistory,
  postGetActivityDetails,
  useGetUserRankDetails,
} from "@/api/clientApi/activity";
import { useDialog } from "@/components/Dialogs/useDialog";
import { useI18n } from "@/i18n/index.ts";
import { useTimestampInSeconds } from "@/lib/hooks/useTimestamp";
import { isMobile } from "@/lib/isMobile";
import { addZero, getTimeRemaining } from "@/lib/utils";
import { getActRule, getActivityState } from "@/stores/activityStore";
import {
  ActivityTypeEnum,
  InfiniteTypeEnum,
} from "@/types/compontent/base/baseEnum";
import {
  GetUserRankDetailsReq,
  RankDetailsPrev,
  UserRankInfo,
} from "@/types/swagger/activity";

const { t } = useI18n();

interface RankDetailsListItem {
  rank: number;
}
export interface WagerContestStore {
  activeTab: number;
  rankDetails: {
    jackpot: number;
    last_time: number;
    list: RankDetailsListItem[];
    max_rank: number;
    prev: RankDetailsPrev;
    page: number;
    page_size: number;
    finish: InfiniteTypeEnum;
  };
  history: {
    page: number;
    page_size: number;
    list: object[];
    start: string;
    end: string;
    finish: InfiniteTypeEnum;
    settle_type: number;
    my_rank: object;
  };
  cycleActiveTab: number;
}

// 投注比赛页数据
interface WagerTabList {
  name: string;
  type: number;
  available: boolean;
}
export interface WagerStore {
  tabActive: number; //选中tab
  rules: string; //规则
  rank: {
    //排名列表
    jackpot: number;
    last_time: number;
    list: UserRankInfo[];
    max_rank: number;
    prev: RankDetailsPrev;
    page: number;
    page_size: number;
    finish: boolean;
    loading: boolean;
  };
  history: {
    //历史弹窗
    page: number;
    page_size: number;
    list: object[];
    start: string;
    end: string;
    finish: boolean;
    time_flag: number;
    my_rank: object;
    loading: boolean;
  };
  myRank: {
    jackpot: number; //奖池金额
    bet_point: number; //投注金额
    win_point: number; //盈利金额
    rank: number; //排名
    prev_point: number; //距离上一名的金额
    reward_point: number; //奖金
    reward_rate: number; //奖金比例
  };
}
// Store 原数据
const initialWagerStore: WagerStore = {
  tabActive: 0,
  rules: "",
  rank: {
    jackpot: 0,
    last_time: 0,
    list: [],
    max_rank: 0,
    prev: {} as RankDetailsPrev,
    page: 1,
    page_size: 12,
    finish: false,
    loading: false,
  },
  history: {
    list: [],
    my_rank: {},
    start: "",
    end: "",
    time_flag: 0,
    page: 1,
    page_size: 12,
    finish: false,
    loading: false,
  },
  myRank: {
    jackpot: 0,
    bet_point: 0,
    win_point: 0,
    rank: -1,
    prev_point: 0,
    reward_point: 0,
    reward_rate: 0,
  },
};

interface WagerContextType {
  wagerContestStore: WagerContestStore;
  setWagerContestStore: (store: WagerContestStore) => void;
  initData: () => void;
  handleChangeCycleTab: (type: number, isHome?: boolean) => void;
  getUserRankDetails: (params: GetUserRankDetailsReq) => Promise<void>;
  countDown: (type?: number) => () => {
    total: number | string;
    day: string;
    hour: string;
    minute: string;
    second: string;
  };
  wagerStore: WagerStore;
  initWagerStore: () => void;
  initHistoryData: () => void;
  onChangeWagerTab: (tab: number) => void;
  getWagerContestRule: (type: number) => void;
  getTabActiveName: () => string;
  openHistory: (atype: number) => void;
  onOpenRulePopup: () => void;
  loadWagerList: () => void;
  getActivityDetails: () => void;
  loadWagerHistoryList: (atype: number) => void;
  getDate: () => string;
  initWagerList: () => void;
  myRankTop: () => number;
}

export const {
  dayList,
  weekList,
  monthList,
  cycleTabList,
  isOpenWagerContest,
} = createRoot(() => {
  const dayList = createMemo(() => {
    const list = [];
    if (getActivityState(ActivityTypeEnum.wagerContestDay)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestDay,
        name: "Wager Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestDayCasino)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestDayCasino,
        name: "Casino Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestDaySport)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestDaySport,
        name: "Sports Contest",
      });
    }
    return list;
  });
  const weekList = createMemo(() => {
    const list = [];
    if (getActivityState(ActivityTypeEnum.wagerContestWeek)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestWeek,
        name: "Wager Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestWeekCasino)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestWeekCasino,
        name: "Casino Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestWeekSport)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestWeekSport,
        name: "Sports Contest",
      });
    }
    return list;
  });
  const monthList = createMemo(() => {
    const list = [];
    if (getActivityState(ActivityTypeEnum.wagerContestMonth)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestMonth,
        name: "Sports Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestMonthCasino)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestMonthCasino,
        name: "Sports Contest",
      });
    }
    if (getActivityState(ActivityTypeEnum.wagerContestMonthSport)) {
      list.push({
        aType: ActivityTypeEnum.wagerContestMonthSport,
        name: "Sports Contest",
      });
    }
    return list;
  });
  const cycleTabList = createMemo(() => {
    const list = [];
    if (dayList().length) {
      list.push({
        type: 0,
        name: t("home.daily"),
        list: dayList(),
      });
    }
    if (weekList().length) {
      list.push({
        type: 1,
        name: t("home.weekly"),
        list: weekList(),
      });
    }
    if (monthList().length) {
      list.push({
        type: 2,
        name: t("home.monthly"),
        list: monthList(),
      });
    }
    return list;
  });
  const isOpenWagerContest = createMemo(() => {
    return dayList().length || weekList().length || monthList().length;
  });

  return {
    dayList,
    weekList,
    monthList,
    cycleTabList,
    isOpenWagerContest,
  };
});

// tab数据
export const wagerTabList = createRoot(() =>
  createMemo(() => {
    const list: WagerTabList[] = [
      {
        name: t("betmatch.daily_contest"),
        type: ActivityTypeEnum.wagerContestDay,
        available: getActivityState(ActivityTypeEnum.wagerContestDay),
      },
      {
        name: t("betmatch.casino_daily"),
        type: ActivityTypeEnum.wagerContestDayCasino,
        available: getActivityState(ActivityTypeEnum.wagerContestDayCasino),
      },
      {
        name: t("betmatch.sports_daily"),
        type: ActivityTypeEnum.wagerContestDaySport,
        available: getActivityState(ActivityTypeEnum.wagerContestDaySport),
      },
      {
        name: t("betmatch.weekly_contest"),
        type: ActivityTypeEnum.wagerContestWeek,
        available: getActivityState(ActivityTypeEnum.wagerContestWeek),
      },
      {
        name: t("betmatch.casino_weekly"),
        type: ActivityTypeEnum.wagerContestWeekCasino,
        available: getActivityState(ActivityTypeEnum.wagerContestWeekCasino),
      },
      {
        name: t("betmatch.sports_weekly"),
        type: ActivityTypeEnum.wagerContestWeekSport,
        available: getActivityState(ActivityTypeEnum.wagerContestWeekSport),
      },
      {
        name: t("betmatch.monthly_contest"),
        type: ActivityTypeEnum.wagerContestMonth,
        available: getActivityState(ActivityTypeEnum.wagerContestMonth),
      },
      {
        name: t("betmatch.casino_monthly"),
        type: ActivityTypeEnum.wagerContestMonthCasino,
        available: getActivityState(ActivityTypeEnum.wagerContestMonthCasino),
      },
      {
        name: t("betmatch.sports_monthly"),
        type: ActivityTypeEnum.wagerContestMonthSport,
        available: getActivityState(ActivityTypeEnum.wagerContestMonthSport),
      },
    ];
    return list?.filter((item) => {
      return item.available;
    });
  }),
);

interface ListTab {
  name: string;
  class: string;
  available: boolean;
}
// 列表Tab
export const getListTab = (isHistory: boolean) =>
  createMemo((): ListTab[] => {
    const list: ListTab[] = [
      {
        name: t("betmatch.rank"),
        class: isMobile()
          ? isHistory
            ? "w-[38px]"
            : "w-[38px]"
          : isHistory
            ? "w-[40px]"
            : "w-[100px]",
        available: true,
      },
      {
        name: t("home.player"),
        class: isMobile()
          ? isHistory
            ? "w-[78px]"
            : "w-[78px]"
          : isHistory
            ? "w-[174px]"
            : "w-[260px]",
        available: true,
      },
      {
        name: t("betmatch.vip_level"),
        class: isMobile()
          ? isHistory
            ? "w-[113px]"
            : "w-[105px]"
          : isHistory
            ? "w-[174px]"
            : "w-[260px]",
        available: isMobile() ? false : true,
      },
      {
        name: t("betmatch.wager"),
        class: isMobile()
          ? isHistory
            ? "w-[113px]"
            : "w-[105px]"
          : isHistory
            ? "w-[174px]"
            : "w-[258px]",
        available: true,
      },
      {
        name: t("betmatch.prize"),
        class: "flex-1 min-w-[100px]",
        available: true,
      },
    ];
    return list?.filter((item) => {
      return item.available;
    });
  })();

export const useWagerContest = () => {
  const [wagerContestStore, setWagerContestStore] =
    createStore<WagerContestStore>({
      activeTab: 0,
      rankDetails: {
        jackpot: 0,
        last_time: 0,
        list: [],
        max_rank: 0,
        prev: {} as RankDetailsPrev,
        page: 0,
        page_size: 12,
        finish: InfiniteTypeEnum.init,
      },
      history: {
        page: 0,
        page_size: 12,
        list: [],
        start: "",
        end: "",
        finish: InfiniteTypeEnum.init,
        settle_type: 0,
        my_rank: {},
      },
      cycleActiveTab: 0,
    });
  const initData = (isHome?: boolean) => {
    handleChangeCycleTab(
      cycleTabList()[wagerContestStore.cycleActiveTab]?.type || 0,
      isHome,
    );
  };
  const handleChangeCycleTab = (type: number, isHome?: boolean) => {
    const atype = cycleTabList()[type]?.list[0]?.aType || 0;
    // if (type == 0) {
    //   atype = dayList()[0]?.aType || 0;
    // } else if (type == 1) {
    //   atype = weekList()[0]?.aType || 0;
    // } else if (type == 2) {
    //   atype = monthList()[0]?.aType || 0;
    // }
    setWagerContestStore("cycleActiveTab", type);
    setWagerContestStore("activeTab", atype);
    if (isHome) {
      const params: GetUserRankDetailsReq = {
        atype,
        has_list: false,
        has_prev: true,
      };
      getUserRankDetails(params);
    }
  };

  const getUserRankDetails = async (params: GetUserRankDetailsReq) => {
    if (!params.atype) return;
    setWagerContestStore("rankDetails", "last_time", 0);
    const res = await useGetUserRankDetails(params);
    if (res) {
      if (res.details) {
        const arr = res.details.list || [];
        let list: RankDetailsListItem[] = [];
        setWagerContestStore("rankDetails", {
          jackpot: res.details.jackpot || 0,
          last_time: res.details.last_time || 0,
          max_rank: res.details.max_rank || 0,
          prev: res.details.prev || {},
          list: res.details.list || [],
        });

        if (params.page == 1) {
          list = arr;
        } else {
          list = [...wagerStore.rank.list, ...list];
        }
        list.sort((a, b) => a.rank - b.rank);
        setWagerContestStore("rankDetails", "list", list);
        if (list.length < wagerStore.rank.page_size) {
          setWagerContestStore(
            "rankDetails",
            "finish",
            InfiniteTypeEnum.complete,
          );
        } else {
          setWagerContestStore(
            "rankDetails",
            "finish",
            InfiniteTypeEnum.loaded,
          );
        }
      }
    }
  };

  // 获取倒计时 type：1 组件 2：详情页
  const countDown = (type: number = 1) => {
    const timeStamp = useTimestampInSeconds();
    return createMemo(() => {
      const lastTime =
        type === 1
          ? wagerContestStore.rankDetails.last_time
          : wagerStore.rank.last_time;
      if (!lastTime)
        return { total: 0, day: "00", hour: "00", minute: "00", second: "00" };
      const total = Math.max(Math.floor(lastTime - timeStamp()), 0);
      if (total > 0) {
        const result = getTimeRemaining(0, total);
        return {
          total: result.total,
          day: addZero(result.day),
          hour: addZero(result.hour),
          minute: addZero(result.minute),
          second: addZero(result.second),
        };
      }
      return { total: 0, day: "00", hour: "00", minute: "00", second: "00" };
    });
  };

  // 投注比赛store
  const [wagerStore, setWagerStore] = createStore<WagerStore>(
    JSON.parse(JSON.stringify(initialWagerStore)),
  );

  const { operateDialog } = useDialog();
  // 初始化数据
  const initWagerStore = () => {
    setWagerStore(reconcile(initialWagerStore));
    const [isInit, setIsInit] = createSignal(false);
    // 处理刷新时获取不到配置的activeTab
    createEffect(
      on(
        createMemo(() => wagerTabList()?.length),
        (len) => {
          if (len) {
            onChangeWagerTab(wagerTabList()?.[0]?.type || 0);
          }
        },
      ),
    );
  };
  // 初始化history
  const initHistoryData = () => {
    setWagerStore("history", reconcile(initialWagerStore.history));
  };

  // 切换选中tab
  const onChangeWagerTab = (type: number) => {
    setWagerStore("tabActive", type);
    initWagerList();
    getActivityDetails();
    if (!isMobile()) {
      getWagerContestRule(type);
    }
  };

  // 获取投注比赛规则
  const getWagerContestRule = async (type: number) => {
    const res = await getActRule(type);
    setWagerStore("rules", res);
  };

  // 获取选中tab的name
  const getTabActiveName = createRoot(() =>
    createMemo((): string => {
      const tabActive: WagerTabList | undefined = wagerTabList().find(
        (item) => item.type === wagerStore.tabActive,
      );
      return tabActive?.name ?? "";
    }),
  );
  // 历史事件
  const openHistory = (atype: number) => {
    initHistoryData();
    operateDialog("HistoryPopup", true, { atype });
  };
  // h5打开规则弹窗
  const onOpenRulePopup = () => {
    getWagerContestRule(wagerStore.tabActive).then(() => {
      initHistoryData();
      operateDialog("H5RulePopup", true);
    });
  };

  // 获取投注比赛列表
  const loadWagerList = async () => {
    if (wagerStore.rank.loading || wagerStore.rank.finish) return;
    setWagerStore("rank", "loading", true);
    const params = {
      atype: wagerStore.tabActive,
      has_list: true,
      has_prev: true,
      page: wagerStore.rank.page,
      page_size: wagerStore.rank.page_size,
    };
    await useGetUserRankDetails(params)
      ?.then((res) => {
        const resList = res.details.list ? res.details.list : [];
        const details = {
          ...res.details,
          list: [...wagerStore.rank.list, ...resList],
        };
        setWagerStore("rank", {
          ...wagerStore.rank,
          ...details,
        });
        if (
          !Array.isArray(res.details.list) ||
          !res.details.list?.length ||
          res.details.list?.length < wagerStore.rank.page_size
        ) {
          setWagerStore("rank", "finish", true);
        } else {
          setWagerStore("rank", "page", wagerStore.rank.page + 1);
        }
      })
      .catch(() => {
        setWagerStore("rank", "finish", true);
      })
      .finally(() => {
        setWagerStore("rank", "loading", false);
      });
  };
  // 获取个人投注信息
  const getActivityDetails = async () => {
    const res = await postGetActivityDetails(
      { atypes: [wagerStore.tabActive] },
      false,
    );
    if (res) {
      const info = res?.activity_details[wagerStore.tabActive]?.details ?? {};
      setWagerStore(
        "myRank",
        reconcile({ ...initialWagerStore.myRank, ...info }),
      );
    }
  };

  // 获取历史-投注比赛列表
  const loadWagerHistoryList = async (atype: number) => {
    if (wagerStore.history.loading || wagerStore.history.finish) return;
    setWagerStore("history", "loading", true);
    const params = {
      atype: atype,
      page: wagerStore.history.page,
      page_size: wagerStore.history.page_size,
    };
    await getUserRankHistory(params)
      ?.then((res) => {
        const resList = res.list ? res.list : [];
        const details = {
          ...res,
          list: [...wagerStore.history.list, ...resList],
        };
        setWagerStore("history", {
          ...wagerStore.history,
          ...details,
        });
        if (
          !Array.isArray(res.list) ||
          !res.list?.length ||
          res.list?.length < wagerStore.history.page_size
        ) {
          setWagerStore("history", "finish", true);
        } else {
          setWagerStore("history", "page", wagerStore.history.page + 1);
        }
      })
      .catch(() => {
        setWagerStore("history", "finish", true);
      })
      .finally(() => {
        setWagerStore("history", "loading", false);
      });
  };
  // 获取时间
  const getDate = createRoot(() =>
    createMemo((): string => {
      const today = new Date();
      const type = wagerStore.tabActive;
      let time: string = "";
      if (
        type == ActivityTypeEnum.wagerContestDay ||
        type == ActivityTypeEnum.wagerContestDayCasino ||
        type == ActivityTypeEnum.wagerContestDaySport
      ) {
        time = `${today.toISOString().split("T")[0]} ~ ${today.toISOString().split("T")[0]}`;
      } else if (
        type == ActivityTypeEnum.wagerContestWeek ||
        type == ActivityTypeEnum.wagerContestWeekCasino ||
        type == ActivityTypeEnum.wagerContestWeekSport
      ) {
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(
          today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1),
        );
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        time = `${startOfWeek.toISOString().split("T")[0]} ~ ${endOfWeek.toISOString().split("T")[0]}`;
      } else if (
        type == ActivityTypeEnum.wagerContestMonth ||
        type == ActivityTypeEnum.wagerContestMonthCasino ||
        type == ActivityTypeEnum.wagerContestMonthSport
      ) {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 2);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1);
        time = `${startOfMonth.toISOString().split("T")[0]} ~ ${endOfMonth.toISOString().split("T")[0]}`;
      }
      return time;
    }),
  );

  // 初始化list列表
  const initWagerList = () => {
    setWagerStore("rank", reconcile(initialWagerStore.rank));
    setWagerStore("rank", "list", []);
    loadWagerList();
  };
  // 我的排名
  const myRankTop = createRoot(() =>
    createMemo(() => {
      const top =
        wagerStore.myRank.rank > 0
          ? wagerStore.myRank.rank - 1 > wagerStore.rank.max_rank
            ? wagerStore.rank.max_rank
            : wagerStore.myRank.rank - 1
          : wagerStore.rank.max_rank;
      return top;
    }),
  );

  return {
    wagerContestStore,
    setWagerContestStore,
    initData,
    handleChangeCycleTab,
    getUserRankDetails,
    countDown,
    wagerStore,
    initWagerStore,
    initHistoryData,
    onChangeWagerTab,
    getWagerContestRule,
    getTabActiveName,
    openHistory,
    onOpenRulePopup,
    loadWagerList,
    getActivityDetails,
    loadWagerHistoryList,
    getDate,
    initWagerList,
    myRankTop,
  };
};

export const WagerContext = createContext<WagerContextType>();

export const useWagerContext = () => {
  const ctx = useContext(WagerContext);
  if (!ctx)
    throw new Error("WagerContext must be used inside a <WagerContext>");
  return ctx;
};
